import { useTranslations } from 'next-intl';

import { markAsViewed } from '@/stores/useNotificationStore';
import { cn } from '@/utils/cn';

import ActionButton from './ActionButton';
import Icons from './Icons';
import { removeNotificationWithRecovery } from './notification-swap-actions';

interface ToastHeaderRightSectionProps {
  id: string | number;
  isHovered: boolean;
  isMobile: boolean;
  isViewed: boolean;
  formattedDate: string | null;
  onClose: () => void;
}

const ToastHeaderRightSection: React.FC<ToastHeaderRightSectionProps> = ({
  id,
  isHovered,
  isMobile,
  isViewed,
  formattedDate,
  onClose,
}: ToastHeaderRightSectionProps) => {
  const t = useTranslations('common.toast');

  if (!formattedDate) {
    return (
      <button
        aria-label={t('close')}
        type="button"
        className="shrink-0 pt-[5px] cursor-pointer transition-opacity hover:opacity-80 active:opacity-50 z-10"
        onClick={onClose}
      >
        <Icons.Close />
      </button>
    );
  }

  const actions = (
    <div className="flex items-center shrink-0 bg-surface-checkbox border border-border-primary rounded-md shadow-[0_1px_250px_0_rgba(22,29,27,0.02)] backdrop-blur-[2px]">
      <ActionButton
        isLeft={true}
        icon={
          isViewed ? (
            <div className="relative">
              <Icons.Square />
              <Icons.Circle className="absolute -top-px -right-px" />
            </div>
          ) : (
            <Icons.Check />
          )
        }
        label={isViewed ? t('markAsUnread') : t('markAsViewed')}
        tooltip={isViewed ? t('markAsUnread') : t('markAsRead')}
        onClick={() => markAsViewed(id, true)}
      />
      <ActionButton
        icon={<Icons.Trash />}
        label={t('removeNotification')}
        tooltip={t('delete')}
        onClick={() => {
          void removeNotificationWithRecovery(id);
        }}
      />
    </div>
  );

  return (
    <div className="flex items-center gap-2 shrink-0">
      {isMobile ? (
        isHovered ? (
          actions
        ) : (
          <p className="text-xs text-text-secondary whitespace-nowrap">{formattedDate}</p>
        )
      ) : (
        <div className="grid items-center">
          <p className="col-start-1 row-start-1 text-xs text-text-secondary whitespace-nowrap group-hover/notification:opacity-0 group-focus-within/notification:opacity-0">
            {formattedDate}
          </p>
          <div className="col-start-1 row-start-1 justify-self-end opacity-0 pointer-events-none group-hover/notification:opacity-100 group-hover/notification:pointer-events-auto group-focus-within/notification:opacity-100 group-focus-within/notification:pointer-events-auto">
            {actions}
          </div>
        </div>
      )}
      <div className={cn('size-[6px] rounded-full', !isViewed && 'bg-blue-100')} />
    </div>
  );
};

export default ToastHeaderRightSection;
